import { Overlay, OverlayPositionBuilder, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { NgClass } from "@angular/common";
import { Component, DestroyRef, inject, input, OnInit, TemplateRef, ViewContainerRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatIcon } from "@angular/material/icon";
import { MatTableDataSource } from "@angular/material/table";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { School } from "src/app-legacy/core/models/school";
import { AppService } from "src/app-legacy/core/services/app.service";
import { AuthService } from "src/app-legacy/core/services/auth.service";
import { SchoolSwitchingConfirmationComponent } from "src/app-legacy/layout/topbar/school-switching-confirmation/school-switching-confirmation.component";
import { EllipsisPipe } from "src/app/shared/pipes/ellipsis.pipe";

@Component({
	selector: "app-topbar-school-switching",
	imports: [MatIcon, EllipsisPipe, ReactiveFormsModule, NgClass],
	templateUrl: "./topbar-school-switching.component.html",
})
export class TopbarSchoolSwitchingComponent implements OnInit {
	private app = inject(AppService);
	private auth = inject(AuthService);
	private overlay = inject(Overlay);
	private overlayPositionBuilder = inject(OverlayPositionBuilder);
	private vcr = inject(ViewContainerRef);
	private modal = inject(NgbModal);
	private destroyRef = inject(DestroyRef);

	internalView = input(false);

	currentSchool = this.app.currentSchool;
	schools = new MatTableDataSource<School>();

	schoolSearch = new FormControl();

	private overlayRef!: OverlayRef;
	isOpen = false;

	get hasMultipleSchools(): boolean {
		return this.schools && this.schools.data.length > 1;
	}

	ngOnInit(): void {
		this.getUserSchools();
	}

	openDropdown(triggerElement: HTMLElement, templateRef: TemplateRef<unknown>): void {
		if (!this.hasMultipleSchools) {
			return;
		}

		const positionStrategy = this.overlayPositionBuilder.flexibleConnectedTo(triggerElement).withPositions([
			{
				originX: "start",
				originY: "bottom",
				overlayX: "start",
				overlayY: "top",
				offsetY: 32,
			},
		]);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			scrollStrategy: this.overlay.scrollStrategies.noop(),
			hasBackdrop: true,
			backdropClass: "cdk-overlay-transparent-backdrop",
		});

		this.overlayRef.attach(new TemplatePortal(templateRef, this.vcr));
		this.overlayRef.backdropClick().subscribe(() => this.closeOverlay());

		this.isOpen = true;
	}

	closeOverlay() {
		if (this.overlayRef) {
			this.isOpen = false;
			this.overlayRef.dispose();
		}
	}

	getUserSchools(): void {
		this.auth.userSchools$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((schools) => {
			this.schools.data = schools;

			this.schoolSearch.valueChanges.subscribe((searchString) => {
				if (!this.schools) return;
				this.schools.filter = searchString;
			});
		});

		this.auth.getUserSchools();
	}

	switchToSchool(targetSchool: School): void {
		this.closeOverlay();

		const modal = this.modal.open(SchoolSwitchingConfirmationComponent, { backdrop: "static", centered: true });
		modal.componentInstance.targetSchool = targetSchool;
		modal.componentInstance.currentSchool = this.currentSchool;

		modal.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.auth
				.switchToSchool(targetSchool.id)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: () => {
						location.reload(); // Reload page after switching school
					},
					error: (error) => {
						this.app.openErrorSnackBar(error);
					},
				});
		});
	}
}
